/** The capital chart (screens §P4.5): reported vs marked CET1 from the start of the game and at each released quarter-end, gap shaded, both appetite floors dashed. One point draws (the start, the floors and the axis). */
import { useSheetMode } from "../theme/Popover";
import { useElementSize } from "../theme/useElementWidth";
import { GxLineChart } from "../theme/LineChart";
import { formatBp, formatKpi } from "../format";
import { GAP_WIDENING_BP, gapBp, type CapitalPoint } from "./derive";

const HEIGHT = 262;
const DESIGN_WIDTH = 596;
/** What the frame keeps under the plot (the "View as table" button and its gap): the plot takes the rest of the card's height. */
const FRAME_CHROME = 44;

export function CapitalChart({ points, floors, widening }: {
  readonly points: readonly CapitalPoint[];
  readonly floors: { readonly reported: number | null; readonly marked: number | null };
  readonly widening: number | null;
}) {
  // The plot fills the room the card has (the two columns of the row stay level), and never drops under its own height.
  const [ref, { width, height: room }] = useElementSize<HTMLDivElement>({ width: DESIGN_WIDTH, height: HEIGHT + FRAME_CHROME });
  const height = Math.max(HEIGHT, room - FRAME_CHROME);
  // On a phone the floors are named by their level alone ("Floor 8.0%"): the legend says which is which, and the plot keeps the width.
  const phone = useSheetMode();
  const thresholds = [
    ...(floors.reported === null ? [] : [{ value: floors.reported * 100, label: phone ? `Floor ${formatKpi("cet1Reported", floors.reported)}` : `Your floor · reported ${formatKpi("cet1Reported", floors.reported)}` }]),
    ...(floors.marked === null ? [] : [{ value: floors.marked * 100, label: phone ? `Floor ${formatKpi("cet1Marked", floors.marked)}` : `Your floor · marked ${formatKpi("cet1Marked", floors.marked)}` }]),
  ];
  // The axis reaches every line it draws, the floors included.
  const levels = [...points.flatMap((p) => [p.reported * 100, p.marked * 100]), ...thresholds.map((t) => t.value)];
  const lo = Math.floor(Math.min(...levels, 9)), hi = Math.ceil(Math.max(...levels, 11));
  const step = hi - lo > 6 ? 2 : 1;
  const yTicks = Array.from({ length: Math.floor((hi - lo) / step) + 1 }, (_, i) => lo + i * step);
  const last = points[points.length - 1];
  const middle = (points.length - 1) / 2;
  const annotation = last === undefined || points.length < 2 ? undefined : {
    at: middle,
    value: ((points[Math.floor(middle)]?.reported ?? 0) * 100 + (points[Math.ceil(middle)]?.marked ?? 0) * 100) / 2,
    text: `gap ${gapBp(last.reported, last.marked)}bp${widening === null || widening < GAP_WIDENING_BP ? "" : ` · ${formatBp(widening, { signed: true })}`}`,
  };
  return (
    <div className="gx-cockpit-capital-chart" data-testid="cockpit.capital.chart">
     <div ref={ref} className="gx-cockpit-capital-fit">
      <GxLineChart
        width={width} height={height} testId="cockpit.capital.svg"
        categories={points.map((p) => p.label)}
        series={[
          { key: "reported", color: "var(--c1)", labelColor: "var(--c1)", values: points.map((p) => p.reported * 100) },
          { key: "marked", color: "var(--c2)", labelColor: "var(--gold-text)", values: points.map((p) => p.marked * 100) },
        ]}
        band={{ upper: "reported", lower: "marked" }}
        thresholds={thresholds}
        yTicks={yTicks}
        formatY={(v) => `${v}%`}
        formatEnd={(v) => formatKpi("cet1Reported", v / 100)}
        {...(annotation === undefined ? {} : { annotation })}
        summary="Reported and marked CET1 at the start of the game and at each released quarter-end, with your two floors"
        table={{ caption: "Reported and marked CET1 by date", head: ["Date", "Reported", "Marked", "Gap"], rows: points.map((p) => [p.label, formatKpi("cet1Reported", p.reported), formatKpi("cet1Marked", p.marked), `${gapBp(p.reported, p.marked)}bp`]) }}
      />
     </div>
    </div>
  );
}
